import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { useApp } from "../../contexts/AppContext";
import { toast } from "sonner";
import { Save, Image as ImageIcon, Music, Volume2, Languages } from "lucide-react";
import AdminContentSection from "../../components/admin/AdminContentSection";

export default function AdminSettings({ variant = "base" }) {
  const { settingsRaw: settings, refreshSettings } = useApp();
  const [form, setForm] = useState(settings);
  const [previewLang, setPreviewLang] = useState("pt");
  const [syncing, setSyncing] = useState(false);
  const dirty = useRef(new Set());
  const isX = variant === "x";

  useEffect(() => {
    if (isX) {
      setForm((prev) => {
        if (!prev) return settings;
        const merged = { ...settings };
        dirty.current.forEach((k) => { merged[k] = prev[k]; });
        return merged;
      });
    } else {
      api.get("/admin/settings/base").then((r) => setForm(r.data)).catch(() => setForm(settings));
    }
  }, [settings, isX]);

  const upload = async (file, field) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      dirty.current.add(field);
      setForm((f) => ({ ...f, [field]: data.path }));
      toast.success("Imagem carregada");
    } catch (e) { toast.error("Upload falhou"); }
  };

  const save = async () => {
    try {
      if (isX) {
        const payload = {};
        dirty.current.forEach((k) => { payload[k] = form[k]; });
        await api.put("/admin/settings_x", payload);
        dirty.current = new Set();
        toast.success("Outras definições guardadas (sobrepõem as Definições)");
      } else {
        await api.put("/admin/settings", form);
        dirty.current = new Set();
        toast.success("Definições guardadas");
      }
      await refreshSettings();
    } catch (e) { toast.error("Erro"); }
  };

  const set = (k, v) => { dirty.current.add(k); setForm((f) => ({ ...f, [k]: v })); };

  const syncEn = async () => {
    if (!window.confirm("Traduzir e copiar TODA a configuração da Home PT para EN?\n\nIsto substitui os textos EN existentes (o logótipo EN é mantido, imagens/música são copiadas e os links mantêm-se). Pode demorar cerca de 30 segundos.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/i18n/sync-pt-to-en");
      toast.success(`EN sincronizado: ${data.translated_fields} campos + ${data.translated_texts} textos + ${data.side_buttons} botões`);
      await refreshSettings();
      if (!isX) { const r = await api.get("/admin/settings/base"); setForm(r.data); dirty.current = new Set(); }
    } catch (e) { toast.error("Falha na sincronização EN"); }
    finally { setSyncing(false); }
  };
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const Toggle = ({ label, field, icon: Icon, color, testId }) => (
    <div className="flex items-center justify-between rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: color + "15", color }}>
          <Icon size={20}/>
        </span>
        <p className="font-outfit font-semibold text-[#0B1D3A]">{label}</p>
      </div>
      <button onClick={() => set(field, !form[field])} data-testid={testId}
        className={`w-14 h-8 rounded-full transition ${form[field] ? "bg-[#005F73]" : "bg-[#0B1D3A]/15"} relative`}>
        <span className={`absolute top-1 ${form[field] ? "left-7" : "left-1"} w-6 h-6 rounded-full bg-white transition-all`} />
      </button>
    </div>
  );

  const PdfUploader = ({ label, field, testId }) => (
    <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5">
      <p className="font-outfit font-semibold text-[#0B1D3A] mb-3 flex items-center gap-2"><ImageIcon size={18}/> {label}</p>
      <div className="flex items-center gap-3 flex-wrap">
        <input type="file" accept="application/pdf"
          onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], field)} className="text-sm" data-testid={testId} />
        {form[field] && (
          <>
            <a href={`${backendUrl}/api/files/${form[field]}`} target="_blank" rel="noreferrer" className="text-sm text-[#005F73] underline">Ver PDF</a>
            <button onClick={() => set(field, "")} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600">Remover</button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-settings-page">
      <button onClick={save} data-testid="settings-save-floating"
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 inline-flex items-center gap-2 px-6 py-3 rounded-full text-white font-bold shadow-2xl transition-colors ${isX ? "bg-[#7B2CBF] hover:bg-[#6a25a6]" : "bg-[#0B1D3A] hover:bg-[#1a2f5a]"}`}>
        <Save size={18}/> {isX ? "Guardar Outras definições" : "Guardar"}
      </button>
      <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] mb-2">{isX ? "Outras definições" : "Definições"}</h1>

      <div className="rounded-2xl bg-[#2D6A4F]/[0.07] border border-[#2D6A4F]/25 px-4 py-3 mb-6 max-w-2xl" data-testid="i18n-sync-box">
        <button onClick={syncEn} disabled={syncing} data-testid="i18n-sync-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#2D6A4F] hover:bg-[#245a42] text-white font-bold transition-colors disabled:opacity-60">
          <Languages size={16} /> {syncing ? "A traduzir PT → EN..." : "Sincronizar EN a partir do PT (traduzir com IA)"}
        </button>
        <p className="text-xs text-[#0B1D3A]/55 mt-2">Copia toda a Home PT para EN e traduz os textos com IA (botões, badge, contagem, rodapé, etc.). <b>Mantém o logótipo EN</b> e os links; copia imagens/música. <b>Substitui</b> os textos EN existentes.</p>
      </div>
      {isX && (
        <div className="rounded-2xl bg-[#7B2CBF]/[0.07] border border-[#7B2CBF]/25 px-4 py-3 mb-6 text-sm text-[#0B1D3A]/80 max-w-2xl" data-testid="settings-x-banner">
          Os valores que <strong>alterar aqui</strong> sobrepõem-se às Definições originais no site. Os campos que <strong>não tocar</strong> continuam a usar as Definições originais.
        </div>
      )}

      <div className="space-y-4 max-w-2xl">
        <h2 className="font-outfit font-bold text-[#0B1D3A]/70 mt-6">Música de Fundo</h2>
        <Toggle label="Música de fundo ativa" field="music_enabled" icon={Music} color="#C1121F" testId="toggle-music-enabled" />
        <div className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 space-y-4">
          <label className="block text-sm">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 font-semibold"><Volume2 size={16} className="text-[#C1121F]" /> Volume</span>
              <span className="text-[#0B1D3A]/60 font-mono" data-testid="music-volume-value">{form.music_volume ?? 20}%</span>
            </div>
            <input type="range" min="0" max="100" step="5" value={form.music_volume ?? 20}
              onChange={(e) => set("music_volume", parseInt(e.target.value))}
              className="w-full mt-2 accent-[#C1121F]" data-testid="music-volume-slider" />
            <span className="text-xs text-[#0B1D3A]/50">Recomendado: 15–30% (moderado/baixo).</span>
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-xl border border-[#0B1D3A]/10 p-3">
              <p className="text-sm font-semibold mb-1">🇵🇹 Música (Português Europeu)</p>
              <input type="file" accept="audio/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "music_path_pt")} className="text-sm" data-testid="music-upload-pt" />
              {form.music_path_pt && (
                <div className="mt-2 flex items-center gap-2">
                  <audio controls src={`${backendUrl}/api/files/${form.music_path_pt}`} className="h-8 max-w-[180px]" />
                  <button onClick={() => set("music_path_pt", "")} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600">Remover</button>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-[#0B1D3A]/10 p-3">
              <p className="text-sm font-semibold mb-1">🇧🇷 Música (Português do Brasil)</p>
              <input type="file" accept="audio/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "music_path_br")} className="text-sm" data-testid="music-upload-br" />
              {form.music_path_br && (
                <div className="mt-2 flex items-center gap-2">
                  <audio controls src={`${backendUrl}/api/files/${form.music_path_br}`} className="h-8 max-w-[180px]" />
                  <button onClick={() => set("music_path_br", "")} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600">Remover</button>
                </div>
              )}
              <p className="text-[10px] text-[#0B1D3A]/40 mt-1">Se vazio, usa o áudio de Português Europeu.</p>
            </div>
            <div className="rounded-xl border border-[#0B1D3A]/10 p-3">
              <p className="text-sm font-semibold mb-1">🇬🇧 Música (English)</p>
              <input type="file" accept="audio/*" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], "music_path_en")} className="text-sm" data-testid="music-upload-en" />
              {form.music_path_en && (
                <div className="mt-2 flex items-center gap-2">
                  <audio controls src={`${backendUrl}/api/files/${form.music_path_en}`} className="h-8 max-w-[180px]" />
                  <button onClick={() => set("music_path_en", "")} className="text-xs px-2 py-1 rounded-md bg-red-50 text-red-600">Remover</button>
                </div>
              )}
            </div>
          </div>
          <p className="text-xs text-[#0B1D3A]/50">Cada versão do site toca o seu próprio áudio (PT Europeu, PT Brasil, English) em loop. A música só começa <strong>depois de o visitante selecionar o idioma</strong> no ecrã de entrada. Os visitantes podem ligar/desligar no botão flutuante (canto inferior esquerdo). Formatos: mp3, m4a, ogg, wav.</p>
        </div>

        <button onClick={save} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#0B1D3A] text-white font-bold" data-testid="settings-save-btn">
          <Save size={16}/> Guardar
        </button>
      </div>

      <AdminContentSection
        form={form} set={set} save={save} PdfUploader={PdfUploader} />
    </div>
  );
}
